import React, {useMemo, useRef} from 'react';
import {connect, useDispatch, useSelector} from 'react-redux';
import {bindActionCreators} from 'redux';
import {Button} from 'react-bootstrap';
import {FmlxButton, FmlxIcon} from 'fmlx-common-ui';
import PropTypes from 'prop-types';
import {getSetupWizardStepperLabel} from 'stores/business/setupWizardBusiness/commonSetupWizardBusiness';
import {getWizardInitialTrayType} from 'stores/selectors/wizardSelectors';
import {checkLoadCellError} from 'stores/business/loadCellErrorBusiness';

import withIntl from '../../lib/withIntl';

import './SetupWizard.scss';
import SetupWizardContent from './SetupWizardContent';
import Stepper from '../../components/Stepper';
import {saveState} from '../../stores/business/stateBusiness';
import {showConfirmationAbortModal} from '../../stores/business/setupWizardBusiness/setupWizardBusiness';
import {
  handleDecrementCurrentWizardView,
  handleIncrementCurrentWizardView,
  setButtonContinueAccessBackward,
  setButtonContinueAccessForward,
} from '../../stores/business/setupWizardBusiness/setupWizardNavigationBusiness';
import {SetupWizardStep, WizardStatus} from '../../constants/SetupWizardEnum';

function mapStateToProps(state) {
  const {currentWizardView, wizardViewData, wizardStatus, isWizardNextAllowed} = state.wizardState;

  return {
    currentWizardViewState: currentWizardView,
    wizardViewDataState: wizardViewData,
    wizardStatusState: wizardStatus,
    isWizardNextAllowedState: isWizardNextAllowed,
    appStateLoading: state.appState.loading.show,
  };
}

const mapDispatchToProps = (dispatch) => ({
  onDecrementCurrentWizardView: bindActionCreators(handleDecrementCurrentWizardView, dispatch),
  onIncrementCurrentWizardView: bindActionCreators(handleIncrementCurrentWizardView, dispatch),
  stateBusinessSaveState: bindActionCreators(saveState, dispatch),
  onShowConfirmationAbortModal: bindActionCreators(showConfirmationAbortModal, dispatch),
  onHandleForwardContinueButtonAccess: bindActionCreators(setButtonContinueAccessForward, dispatch),
  onHandleBackwardContinueButtonAccess: bindActionCreators(setButtonContinueAccessBackward, dispatch),
});

function SetupWizard({
  stateBusinessSaveState,
  onDecrementCurrentWizardView,
  onIncrementCurrentWizardView,
  currentWizardViewState,
  onShowConfirmationAbortModal,
  onHandleForwardContinueButtonAccess,
  onHandleBackwardContinueButtonAccess,
  wizardStatusState,
  appStateLoading,
  isWizardNextAllowedState,
  wizardViewDataState,
  intl,
}) {
  const dispatch = useDispatch();
  const initialTrayType = useSelector(getWizardInitialTrayType);
  const contentRef = useRef(null);
  const stepperLabel = useMemo(() => getSetupWizardStepperLabel(initialTrayType), [initialTrayType]);

  const handleClickNext = async (skipSubstep = false) => {
    const isLoadCellError = await dispatch(checkLoadCellError(null, () => handleClickNext(skipSubstep)));
    if (isLoadCellError) return;
    const navigationOptions = await contentRef.current.onContinue(skipSubstep);
    const {isChangeStep, isCheckContinueButtonAccess, isSkipSubstep} = navigationOptions;

    if (isChangeStep) await onIncrementCurrentWizardView(skipSubstep || isSkipSubstep, isCheckContinueButtonAccess);
    else if (isCheckContinueButtonAccess) await onHandleForwardContinueButtonAccess(skipSubstep);

    await stateBusinessSaveState();
  };

  const handleClickBack = async () => {
    const navigationOptions = await contentRef.current.onBack();
    const {isChangeStep, isCheckContinueButtonAccess} = navigationOptions;

    if (isChangeStep) await onDecrementCurrentWizardView(isCheckContinueButtonAccess);
    else if (isCheckContinueButtonAccess) await onHandleBackwardContinueButtonAccess();

    await stateBusinessSaveState();
  };

  const isSkipButtonShown = () => {
    const {isSkippable} = wizardViewDataState[currentWizardViewState] || {};

    return isSkippable;
  };

  const getSkippedStep = () => {
    const filterdData = Object.values(wizardViewDataState).filter(
      (data) =>
        data.stepName !== SetupWizardStep.WELCOME_SCREEN &&
        data.stepName !== SetupWizardStep.REPORT &&
        data.stepName !== SetupWizardStep.FINISH_SCREEN &&
        data.isStepProcessed === false,
    );

    return filterdData.map((data) => data.step);
  };

  const getProcessedStep = () => {
    const filterdData = Object.values(wizardViewDataState).filter(
      (data) =>
        data.stepName !== SetupWizardStep.WELCOME_SCREEN &&
        data.stepName !== SetupWizardStep.REPORT &&
        data.stepName !== SetupWizardStep.FINISH_SCREEN &&
        data.isStepProcessed,
    );

    return filterdData.map((data) => data.step);
  };

  const getAbortButtonClassName = () => {
    const currentStepData = wizardViewDataState[currentWizardViewState];
    const isOnFinishScreen = currentStepData?.stepName === SetupWizardStep.FINISH_SCREEN;

    return `icon-abort ${appStateLoading || isOnFinishScreen ? 'hidden' : ''}`;
  };

  return (
    <div className="wizard-container">
      <span className={getAbortButtonClassName()}>
        <FmlxButton
          onlyIcon
          icon={<FmlxIcon name="CloseOutline" />}
          variant="plain"
          type="basic"
          withTooltip={false}
          onClick={() => onShowConfirmationAbortModal()}
        />
      </span>
      <div
        className={`header-container ${(wizardStatusState === WizardStatus.NOT_STARTED || wizardStatusState === WizardStatus.COMPLETED) && 'hidden'}`}
      >
        <Stepper
          stepsLabel={stepperLabel}
          width="70vw"
          height="6vw"
          wrapLabel
          currentWizardView={currentWizardViewState}
          decreaseSizeBy={3}
          isResponsive
          skippedStep={getSkippedStep()}
          processedStep={getProcessedStep()}
        />
      </div>
      <SetupWizardContent
        ref={contentRef}
        currentWizardViewState={currentWizardViewState}
        wizardStatusState={wizardStatusState}
      />
      <div className={`footer-container ${wizardStatusState === WizardStatus.NOT_STARTED ? 'hidden' : ''}`}>
        <Button
          className={`footer-button footer-left button-secondary disabled-${appStateLoading}`}
          onClick={() => handleClickBack()}
          disabled={appStateLoading}
        >
          {intl.formatMessage({id: 'setupWizard.footer.firstButton'})}
        </Button>
        <span className="footer-right">
          {isSkipButtonShown() && (
            <Button
              style={{width: '15%', display: appStateLoading && 'none'}}
              className="footer-button button-tertiary footer-right--button-skip"
              onClick={() => handleClickNext(true)}
            >
              <span style={{fontSize: '1vw'}}>{intl.formatMessage({id: 'setupWizard.footer.skipButton'})}</span>
            </Button>
          )}
          <Button
            key={isWizardNextAllowedState}
            disabled={!isWizardNextAllowedState || appStateLoading}
            className={`footer-button button-primary footer-right--button-continue disabled-${!isWizardNextAllowedState || appStateLoading}`}
            onClick={() => handleClickNext()}
          >
            {intl.formatMessage({id: 'setupWizard.footer.secondButton.continue'})}
          </Button>
        </span>
      </div>
    </div>
  );
}

SetupWizard.propTypes = {
  stateBusinessSaveState: PropTypes.func.isRequired,
  onDecrementCurrentWizardView: PropTypes.func.isRequired,
  onIncrementCurrentWizardView: PropTypes.func.isRequired,
  currentWizardViewState: PropTypes.number.isRequired,
  onShowConfirmationAbortModal: PropTypes.func.isRequired,
  onHandleForwardContinueButtonAccess: PropTypes.func.isRequired,
  onHandleBackwardContinueButtonAccess: PropTypes.func.isRequired,
  wizardStatusState: PropTypes.any.isRequired,
  appStateLoading: PropTypes.any.isRequired,
  isWizardNextAllowedState: PropTypes.bool.isRequired,
  wizardViewDataState: PropTypes.any.isRequired,
  intl: PropTypes.any.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(withIntl(SetupWizard));
